import { useEffect, useReducer } from 'react';
import { useLocation } from 'react-router-dom';
import { useFlag } from '@unleash/proxy-client-react';

import { selectUser } from 'store';

import { useAppSelector } from 'hooks';
import { FeatureFlag } from 'utils/enums';
import { session, SESSION_KEYS } from 'utils/storage';

import {
  HIDDEN_ZOOM_CHAT_PATHS,
  ZOOM_CHAT_CAMPAIGN_API_KEY,
  ZOOM_CHAT_ENABLE_OVERRIDE_PARAM,
  ZOOM_CHAT_ENGAGEMENT_ENDED_EVENT
} from './zoomChat.constants';
import { onZoomChatSdkAvailable } from './zoomChatSdk';

/**
 * Whether the override has been seen on in this document, and kept from then on. The param is on the
 * URL of the page load, and the first click inside the app drops it. The slot now outlives page
 * changes, so an override that followed the URL would read as the channel going off at that click,
 * and the slot's eviction edge would reload the page and take away the chat the tester switched
 * on. Module scope, for the reason the gate state below is: what it keeps on is this document's
 * client. Only ever set outside the live environment, and removing the param from the URL later
 * does not clear it: only a new document does.
 */
let overrideSeenOn = false;

/**
 * The tester's override. It is off only where `VITE_ENV` is 'production', so it works in any other
 * build, not only in a PR preview. In the live environment the flags are the only answer, so
 * switching one off cannot be undone with a query string.
 *
 * Deliberately `VITE_ENV`, not `import.meta.env.PROD` and not `IS_LIVE` from `constants/app.ts`.
 * `PROD` is true on every CI path — the build script runs a bare `vite build`, so previews are
 * "production" mode too — which makes it useless as an environment discriminator and would
 * silently kill this override everywhere. `IS_LIVE` is the identical expression but evaluated at
 * module load, so importing it would break the tests that stub `VITE_ENV`. Read it at call time.
 */
const isZoomChatOverrideOn = (): boolean => {
  if (import.meta.env.VITE_ENV === 'production') {
    return false;
  }

  if (!overrideSeenOn) {
    try {
      overrideSeenOn =
        new URLSearchParams(window.location.search).get(ZOOM_CHAT_ENABLE_OVERRIDE_PARAM) === '1';
    } catch {
      // An unreadable URL leaves the override off.
    }
  }

  return overrideSeenOn;
};

/**
 * The `wismo-chat` flag's say over the channel (LMD-1408, Dominique's third criterion: the Zoom
 * chat rolls out, and off, under the same flag as the AI chat), and the one exception to it: a
 * human chat that is live at the moment the flag reads off finishes. Zoom's SDK is the source of
 * truth for "live" — `isChatting`, polled at that moment and not again — so an idle widget is
 * evicted like it is when the channel flag turns off, and once the carried engagement ends nothing
 * new is allowed while the flag is off. A flag that reads on again releases the exception.
 *
 * Module scope, for the reason clearZoomChatIdentity keeps `evictionOwed` there: what is carried
 * is the SDK's engagement, whose lifetime is this document's, and the slot's eviction edge and the
 * visibility gate must read one answer. A document that loads with the flag already off never
 * carries anything: the flag was never read on in it.
 */
let wismoChatLastRead: 'off' | 'on' | null = null;
let engagementCarried = false;
const gateWakers = new Set<() => void>();
const sdksWatchedForEnd = new WeakSet<object>();

const readWismoChatGate = (isWismoChatOn: boolean): boolean => {
  if (isWismoChatOn) {
    wismoChatLastRead = 'on';
    engagementCarried = false;

    return true;
  }

  // The moment it reads off: a live engagement is carried, anything else is not.
  if (wismoChatLastRead === 'on') {
    engagementCarried = Boolean(window.zoomCampaignSdk?.isChatting);
  }

  wismoChatLastRead = 'off';

  return engagementCarried;
};

// The carried engagement has ended, from either side: the exception is over, and every mounted
// gate re-reads so the slot's eviction edge sees the channel go off.
const releaseCarriedEngagement = () => {
  if (!engagementCarried) {
    return;
  }

  engagementCarried = false;
  gateWakers.forEach((wake) => wake());
};

/**
 * Subscribes to the carried engagement's end, then reconciles with the SDK's own state
 * (second-pass review): the render snapshot that started the carry and this subscription — a
 * passive effect, run after paint — are two moments, and an engagement that ended between them
 * fired its event to nobody. Re-reading `isChatting` once the listener is attached closes that
 * gap, and the same order covers an SDK that becomes available only after the shutoff, since both
 * reach here through onZoomChatSdkAvailable. The release takes the event handler's own path.
 *
 * One listener per SDK for its whole life, like the routing hints cleanup; a vendor emitter that
 * rejects the subscription leaves the carried widget until the next document load.
 */
const watchCarriedEngagement = (sdk: ZoomCampaignSdk) => {
  if (!sdksWatchedForEnd.has(sdk)) {
    try {
      sdk.on(ZOOM_CHAT_ENGAGEMENT_ENDED_EVENT, releaseCarriedEngagement);
      sdksWatchedForEnd.add(sdk);
    } catch {
      // Degraded, not broken: the flag still reads off on the next document load.
    }
  }

  if (!sdk.isChatting) {
    releaseCarriedEngagement();
  }
};

/**
 * Whether the chat channel is switched on.
 *
 * Shared by the visibility gate and the eviction edge in `ZoomChatSlot` so both read one answer;
 * two independent reads of the flags could disagree and leave a client loaded with nothing
 * watching for it to be turned off.
 *
 * Two flags: the channel's own (`zoom-chat-widget`) and `wismo-chat`, with the live-engagement
 * exception above. Off, this hook drives the slot's eviction reload — for the channel flag at once,
 * for `wismo-chat` at once for an idle widget and once a carried engagement ends.
 */
export const useZoomChatEnabled = (): boolean => {
  const isChannelOn = useFlag(FeatureFlag.ZoomChatWidget);
  const isWismoChatOn = useFlag(FeatureFlag.AiAssistantChat);
  const [, wake] = useReducer((tick: number) => tick + 1, 0);
  const isAllowedByWismoChat = readWismoChatGate(Boolean(isWismoChatOn));
  const isCarrying = !isWismoChatOn && isAllowedByWismoChat;

  useEffect(() => {
    gateWakers.add(wake);

    return () => {
      gateWakers.delete(wake);
    };
  }, []);

  // Watches the carried engagement for its end, and reconciles with the SDK as soon as it can;
  // armed only while one is carried.
  useEffect(() => {
    if (!isCarrying) {
      return undefined;
    }

    return onZoomChatSdkAvailable(watchCarriedEngagement);
  }, [isCarrying]);

  if (isChannelOn && isAllowedByWismoChat) {
    return true;
  }

  return isZoomChatOverrideOn();
};

/**
 * Whether a route is one where the bubble stays hidden: the exact path, or the path followed by
 * "/". It drives only the bubble (`useZoomChatVisibility` here, the hide and show calls in
 * `useZoomChatBubbleVisibility`, and the script latch in `ZoomChatWidget`); it never decides whether
 * the chat is mounted, which is `useZoomChatAllowed`.
 */
export const isHiddenZoomChatPath = (pathname: string): boolean =>
  HIDDEN_ZOOM_CHAT_PATHS.some((hidden) => pathname === hidden || pathname.startsWith(`${hidden}/`));

/**
 * Whether the Zoom Contact Center chat may be mounted at all, on any route. This is the mount gate
 * for `ZoomChatSlot`: the widget and the credential hook it registers are mounted only while this
 * is true, and they stay mounted across ordinary page changes while it holds, so a chat started on
 * any page of a signed-in patient finds the identity hook. App's loader and a session change can
 * still remount them. The identity watchdog is armed by the SDK loader inside the widget, so it
 * starts only once the script is injected or already in the document. Which pages show the bubble
 * is separate and does not belong here (`isHiddenZoomChatPath`). Never mounts inside the mobile
 * app's WebViews; the Flutter shell exposes its own support surfaces.
 *
 * Note there is no longer a credential gate here in any meaningful sense: the campaign apikey is a
 * committed constant, so the check below is true in every build by construction. Whether a bubble
 * appears at all on a given host is decided Zoom-side by Campaign Location URL matching, and those
 * rules are in place (since Aug 11, see ZOOM_CHAT_CAMPAIGN_API_KEY): `localhost` and any unlisted
 * host match no campaign and get nothing, whatever this hook says.
 *
 * The `zoom-chat-widget` flag is the off-switch for the channel itself, and it is deliberately
 * separate from the two gates that resemble one but are not. Credential presence was never a
 * release gate: the two Zoom env vars it used to read were repository-scoped, so they were present
 * in every build including PR builds that resolve to no GitHub Environment — and now the apikey is
 * a constant, so it is present by construction. And the Convex-side identity switch degrades
 * identity alone: chat, and so whatever the patient types, keeps flowing to the vendor either way.
 * This gate stops the channel. So does `wismo-chat` (LMD-1408), through `useZoomChatEnabled`, with
 * the one exception described there: a human chat live at the moment it reads off finishes.
 *
 * It fails closed: Unleash resolves a flag to false until it has fetched, and while it is
 * unreachable. An outage therefore costs us the chat surface rather than leaving a vendor-facing
 * PHI channel with no remote way to switch it off, which is the right direction for this surface.
 * The visible consequence is that the bubble appears a moment after the rest of the page.
 */
export const useZoomChatAllowed = (): boolean => {
  const { accessToken, userId } = useAppSelector(selectUser) ?? {};
  const isEnabled = useZoomChatEnabled();

  const isConfigured = Boolean(ZOOM_CHAT_CAMPAIGN_API_KEY);
  const isInWebView = Boolean(window.flutter_inappwebview);
  // Staff signed in as the patient never get the channel, for the reason the AI chat is hidden from
  // them (Mason, Sept 23): the widget would mint the patient's verified identity for Zoom, and an
  // agent would see a verified patient where a staff member is typing. The tab's view-as-patient
  // marker decides it, as it does for the AI chat; Convex sees only the portal token and cannot.
  const isStaffViewingAsPatient = Boolean(session.get(SESSION_KEYS.VIEW_AS_PATIENT));
  // A patient id alone is not a session. The id is persisted across tabs while the tokens live in
  // the tab's session, so a tab reopened after it was closed without signing out has the id and no
  // token until the router sends it to Login. The slot sits outside the protected routes, so without
  // the token here it would load Zoom, with whatever Zoom kept for that patient, on a signed-out page.
  const hasSession = Boolean(userId) && Boolean(accessToken);

  return (
    Boolean(isEnabled) && isConfigured && !isInWebView && !isStaffViewingAsPatient && hasSession
  );
};

/**
 * Whether the chat bubble may show on the current route: the chat is allowed and the route is not
 * hidden. Shows portal-wide for authenticated patients, except the denylisted routes (live video
 * visits, auth/intake/checkout flows). Kept as the one answer for the surfaces that offer the chat
 * to the patient, such as the AI assistant's live-agent offer, which must not point at a bubble the
 * patient cannot see.
 */
export const useZoomChatVisibility = (): boolean => {
  const pathname = useLocation()?.pathname ?? '/';
  const isAllowed = useZoomChatAllowed();

  return isAllowed && !isHiddenZoomChatPath(pathname);
};
